import { useSelector } from 'react-redux';
import l300 from '../../../assets/hwrenders/L300.png';
import l300Chine from '../../../assets/hwrenders/L300_Chine.png';
import l450 from '../../../assets/hwrenders/L450.png';
import l450Chine from '../../../assets/hwrenders/L450_Chine.png';

import { faCartShopping, faHourglass } from '@fortawesome/pro-regular-svg-icons';
import { PrimaryButton } from 'component-library';
import { useMemo } from 'react';
import autoListBlack from '../../../assets/icons/Auto List - original - 192x192 - Icon Black.png';
import autoListWhite from '../../../assets/icons/Auto List - original - 192x192 - Icon White.png';
import autoTrimBlack from '../../../assets/icons/Auto Trim - original - 192x192 - Icon Black.png';
import autoTrimWhite from '../../../assets/icons/Auto Trim - original - 192x192 - Icon White.png';
import coordinatedTurnBlack from '../../../assets/icons/Coordinated turn - original  192x192 - Icon Black.png';
import coordinatedTurnWhite from '../../../assets/icons/Coordinated turn - original  192x192 - Icon White.png';
import driverIntentionBlack from '../../../assets/icons/Driver Intention - 192x192 - Icon Black.png';
import driverIntentionWhite from '../../../assets/icons/Driver Intention - 192x192 - Icon White.png';
import pitchControlBlack from '../../../assets/icons/Pitch Control - original - 192 x 192 - Icon Black.png';
import pitchControlWhite from '../../../assets/icons/Pitch Control - original - 192 x 192 - Icon White.png';
import rollControlBlack from '../../../assets/icons/Roll control - original - 192x192 - Icon Black.png';
import rollControlWhite from '../../../assets/icons/Roll control - original - 192x192 - Icon White.png';
import trueTurnBlack from '../../../assets/icons/True Turn - 192x192 - Icon Black.png';
import trueTurnWhite from '../../../assets/icons/True Turn - 192x192 - Icon White.png';
import { useTheme } from '../../../contexts/ThemeContext';
import { BoatModel, Package, SystemDetail } from '../../../models/ApiModels';
import { RootState } from '../../../store/reducers/combine';
import BoatDetailsImage from '../../BoatModels/BoatDetails/BoatDetailsImage';
import { SystemDetailsConnectBoatModelToSystemModal } from './SystemDetailsConnectBoatModelToSystem';
import SystemDetailsCTACheckList from './SystemDetailsCTAChecklist';

interface SystemDetailsCTAProps {
  system: SystemDetail;
  oldSystem: SystemDetail;
  setSystem: (system: SystemDetail) => void;
  editMode: boolean;
  DiscardChanges: () => void;
  updateSystemAndClose: (updatedSystem: SystemDetail) => void;
  hasChanged: boolean;
  setShowUpgradeLicense: (value: boolean) => void;
  openFunctionTrialPopup: (value: boolean) => void;
}

export const SystemDetailsCTA = ({
  system,
  editMode,
  setSystem,
  oldSystem,
  DiscardChanges,
  updateSystemAndClose,
  hasChanged,
  setShowUpgradeLicense,
  openFunctionTrialPopup,
}: SystemDetailsCTAProps) => {
  const { resolvedTheme } = useTheme();
  const items = useSelector((state: RootState) => state.configurations.items);
  const packages = useSelector((state: RootState) => state.configurations.packages);
  const boatModels = useSelector((state: RootState) => state.boatModels.boatModels);
  const boatModel = boatModels.find((boat: BoatModel) => boat.id === system.boatModelId);
  const functionPackage = packages.find((pkg: any) => pkg.id === system.packageId);
  const servo = items.find(
    (item: any) => item.articleNumber === system.lightningHardwareSystem?.starboardServo?.articleNumber?.slice(0, -3)
  );

  const systemHWSrc = () => {
    const servoName = servo?.displayName || '';

    switch (true) {
      case servoName.includes('L450'):
        return l450;
      case servoName.includes('L450 Chine'):
        return l450Chine;
      case servoName.includes('L300'):
        return l300;
      case servoName.includes('L300 Chine'):
        return l300Chine;
      default:
        return '';
    }
  };

  type PackageCardInfo = {
    desc: string;
    bg: string;
    icons?: any;
  };

  const handlePackageCard = (): PackageCardInfo => {
    switch (functionPackage?.displayName) {
      case 'Ultimate':
        return {
          desc: 'This system is equipped with the full Humphree experience',
          bg: 'bg-[#F56432]',
          icons: [
            { src: resolvedTheme === 'light' ? autoListWhite : autoListBlack, alt: 'Auto List' },
            { src: resolvedTheme === 'light' ? autoTrimWhite : autoTrimBlack, alt: 'Auto Trim' },
            { src: resolvedTheme === 'light' ? coordinatedTurnWhite : coordinatedTurnBlack, alt: 'Coordinated Turn' },
            { src: resolvedTheme === 'light' ? driverIntentionWhite : driverIntentionBlack, alt: 'Driver Intention' },
            { src: resolvedTheme === 'light' ? trueTurnWhite : trueTurnBlack, alt: 'True Turn' },
            { src: resolvedTheme === 'light' ? pitchControlWhite : pitchControlBlack, alt: 'Pitch Control' },
            { src: resolvedTheme === 'light' ? rollControlWhite : rollControlBlack, alt: 'Roll Control' },
          ],
        };
      case 'Cruise':
        return {
          desc: 'Great for cruising around',
          bg: 'bg-black',
          icons: [
            { src: resolvedTheme === 'light' ? autoListWhite : autoListBlack, alt: 'Auto List' },
            { src: resolvedTheme === 'light' ? autoTrimWhite : autoTrimBlack, alt: 'Auto Trim' },
            { src: resolvedTheme === 'light' ? coordinatedTurnWhite : coordinatedTurnBlack, alt: 'Coordinated Turn' },
          ],
        };
      case 'Dynamic Plus':
        return {
          desc: '',
          bg: 'bg-black',
          icons: [],
        };
      case 'Dynamic':
        return {
          desc: '',
          bg: 'bg-black',
          icons: [],
        };
      case 'Comfort Plus':
        return {
          desc: '',
          bg: 'bg-black',
          icons: [],
        };
      case 'Comfort':
        return {
          desc: '',
          bg: 'bg-black',
          icons: [],
        };
      default:
        return {
          desc: '',
          bg: 'bg-black',
          icons: [],
        };
    }
  };

  const hasUpgrades = useMemo(() => {
    if (system.packageId === '') return true; // Manual package, has upgrade

    if (!system.addonFeatureIds.includes('FEAT-7-1')) return true; // Underwater lights not included, has upgrade

    const currentPackage: Package | undefined = packages.find((pack: Package) => pack.id === system.packageId);
    if (currentPackage && currentPackage.availableUpgrades.length > 0) return true; // Available upgrades exist

    return false; // No upgrades
  }, [system.packageId, system.addonFeatureIds, packages]);

  return (
    <div className='xl:flex'>
      <div className='flex-1 grid lg:grid-cols-2 gap-4 items-stretch min-h-[300px] lg:min-h-[500px]'>
        {/* boat image card */}
        <div className='relative flex justify-center h-full min-h-[300px] lg:min-h-[500px]'>
          {oldSystem.boatModelId ? (
            <>
              <div className='absolute z-10 left-0 top-0 mt-8 ml-8'>
                <h5 className='prose-heading5 color-black'>{boatModel?.make}</h5>
                <h4 className='prose-heading4 color-black'>{boatModel?.name}</h4>
              </div>
              <BoatDetailsImage boatModelId={system.boatModelId} edit={editMode} />
            </>
          ) : (
            <SystemDetailsConnectBoatModelToSystemModal
              system={system}
              setSystem={setSystem}
              oldSystem={oldSystem}
              DiscardChanges={DiscardChanges}
              updateSystemAndClose={updateSystemAndClose}
              hasChanged={hasChanged}
            />
          )}
        </div>
        <div className={`grid ${systemHWSrc() ? 'grid-rows-2' : ''} gap-4 items-stretch text-white`}>
          {/* function package card */}
          <div className={`${handlePackageCard().bg} p-8 justify-between flex flex-col gap-2`}>
            <div>
              <span className='prose-heading3'>{functionPackage?.displayName || 'Manual'}</span>
              <p className='prose-paragraphBase'>{handlePackageCard().desc}</p>
              <div className='flex flex-wrap'>
                {handlePackageCard().icons?.map((icon: { src: string; alt: string }) => (
                  <img className='max-h-14 object-contain' key={`icon-${icon.alt}`} src={icon.src} alt={icon.alt} />
                ))}
              </div>
            </div>
            {hasUpgrades && (
              <div className='flex flex-col flex-wrap gap-2'>
                <PrimaryButton
                  label='Activate function trial'
                  icon={faHourglass}
                  onClick={() => openFunctionTrialPopup(true)}
                />
                <PrimaryButton
                  label='Buy available upgrades'
                  icon={faCartShopping}
                  onClick={() => setShowUpgradeLicense(true)}
                />
              </div>
            )}
          </div>
          {/* hw card */}
          {systemHWSrc() && (
            <div className='flex flex-col bg-gray-20 p-8'>
              {servo?.displayName && <span className='prose-heading3'>{servo.displayName}</span>}
              {servo?.articleNumber && <span className='prose-body'>{servo.articleNumber}</span>}
              <img src={systemHWSrc()} alt='hw render' className='object-cover' />
            </div>
          )}
        </div>
      </div>
      <div className='py-8 xl:px-8'>
        <h5 className='prose-heading5 mb-3'>Checklist</h5>
        <SystemDetailsCTACheckList system={system} />
      </div>
    </div>
  );
};
